/**
 * Members & credits API wrapper (v2 — Project Independence, DNKADM-0059).
 *
 * Re-points the v1 `/admin/members` reads/writes to the v2 **bearer-only**
 * `/v2/admin/members/**` surface (handoff §5.14). Members live in the external
 * contact system; `contactId` is that system's contact id — DATA, NEVER surfaced
 * as "GHL …" (§4.0 / CLAUDE.md white-label).
 *
 * The v2 model differs from v1 in three load-bearing ways:
 *  - **Credits are bucket-ID-keyed.** Balances, lots, ledger, and admin adjustments
 *    all reference a first-class **`creditBucketId`** (the 0052 `v2_credit_bucket`
 *    id), not a service-category STRING as in v1. The whole-bucket / multi-bucket
 *    adjust and the per-lot ops (`creditLots.ts`) key off the bucket id.
 *  - **Membership references a plan id.** A `membershipPlanId` (the 0056
 *    `v2_membership_plan` id — the plan IS the tier) replaces the v1 tier STRING.
 *    The effective active/grace state is derived server-side (display only).
 *  - **Coach references a staff id.** A `v2_staff.id` (the 0053 entity), NOT a
 *    contact-system user id.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.14, §3.1 — standard REST; ASSUMED standard shapes,
 * recorded in the DNKADM-0059 delivery's Notes):**
 *  - `GET  /v2/admin/members?page&size&search` → a paged `{ content, page, size, total }`.
 *  - `GET  /v2/admin/members/{contactId}` → one {@link MemberDetail}.
 *  - `GET  /v2/admin/members/{contactId}/credits` → per-bucket {@link CreditBalance}`[]`.
 *  - `POST /v2/admin/members/{contactId}/credits/adjust` ← single/multi bucket grant.
 *  - `PUT  /v2/admin/members/{contactId}/credits/{bucketId}/reduce` ← bucket reduce.
 *  - `GET  /v2/admin/members/{contactId}/membership` → current membership read.
 *  - `GET  /v2/admin/members/{contactId}/staff` (coach) | `PUT` ← `{ staffId }`.
 *  - `GET  /v2/admin/members/{contactId}/profile-values` | `PUT` ← `{ values }`.
 *
 * Pagination (§10): `?page=&size=` with a `{ content, page, size, total }` envelope,
 * tolerantly parsed (a bare array / `members`/`content` wrapper all degrade) — adapt
 * on integration. Every read is parsed DEFENSIVELY (mirrors `services.ts`): a
 * missing/wrong-typed field degrades, never a throw.
 */
import { request } from './client';
import { toWirePage, toUiPage } from './paging';
import { ApiError } from './errors';
import { LOT_DAY_WIRE } from './creditLots';

/** v2 admin members base path (bearer surface). */
const PATH = '/v2/admin/members';

/** One member row in the list response. Any field may be null on the wire. */
export interface MemberSummary {
  /** The contact id (external contact-system id) — DATA, never surfaced as "GHL …". */
  id: string | null;
  name: string | null;
  email: string | null;
  /** The member's plan/tier display name (server-resolved), or null. */
  membershipTier: string | null;
  membershipStatus: string | null;
  /** Raw subscription status (`active`/`paused`/`past_due`/`canceled`/`creating`), display-only (DNKAPI-0600). */
  subscriptionStatus: string | null;
  /** When an at-period-end cancel was scheduled (ISO instant), or null (DNKAPI-0600). */
  cancelScheduledAt: string | null;
  /** The last day of access (`YYYY-MM-DD`, expiration + grace days), or null (DNKAPI-0600). */
  effectiveAccessEndsOn: string | null;
  /**
   * Bay-credit balance for the low-credit highlight (assumed additive field). Null
   * when absent/wrong-typed — the column degrades to `—`.
   */
  bayCredits: number | null;
  /**
   * Last-visit instant, ISO date string (assumed additive field). Null when
   * absent/wrong-typed — the column degrades to `—`.
   */
  lastVisit: string | null;
}

/**
 * Parsed `GET /v2/admin/members` page (§10 envelope). `total` is null when upstream
 * omits it (the table then cannot assume a last page).
 */
export interface MembersPage {
  members: MemberSummary[];
  page: number;
  size: number;
  total: number | null;
}

/**
 * One credit bucket balance (§5.14) — keyed by **`creditBucketId`** (the 0052
 * `v2_credit_bucket` id), not a category string as in v1. `bucketName` is the
 * server-resolved display name (the picker offers names; the id is the key).
 */
export interface CreditBalance {
  /** The bucket id — the key every credit op references. Null only on a malformed row. */
  creditBucketId: string | null;
  /** The bucket display name, or null when absent. */
  bucketName: string | null;
  /** Ledger balance for the bucket (the source of truth). */
  balance: number;
}

/** One dated credit hint on the forward-contract credit-summary endpoint. */
interface CreditSummaryHint {
  amount: number;
  date: string;
}

/**
 * One per-bucket summary row from
 * `GET /v2/admin/members/{contactId}/credit-summary` (DNKAPI-0288 forward
 * contract). `available` is the read-card balance; dated hints are null when the
 * server omits them or sends a partial/malformed hint.
 */
export interface CreditSummaryEntry {
  bucketId: string;
  bucketName: string | null;
  available: number;
  expiringSoon: CreditSummaryHint | null;
  arrivingSoon: CreditSummaryHint | null;
}

/** Parsed `GET /v2/admin/members/{contactId}` detail. */
export interface MemberDetail {
  /** The contact id (external contact-system id) — DATA, never surfaced as "GHL …". */
  id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  /** Membership-since instant, ISO date string. Null when absent/wrong-typed. */
  joinedAt: string | null;
  /**
   * The member's plan/tier display name (server-resolved), or null. Read from the nested
   * `membership.tier` (DNKAPI-0600: the detail never sent a top-level tier).
   */
  membershipTier: string | null;
  /** The EFFECTIVE status, from the nested `membership.effectiveStatus` (DNKAPI-0600). */
  membershipStatus: string | null;
  /** Nested `membership.subscriptionStatus`, display-only (DNKAPI-0600). */
  subscriptionStatus: string | null;
  /** Nested `membership.cancelScheduledAt` (ISO instant), or null (DNKAPI-0600). */
  cancelScheduledAt: string | null;
  /** Nested `membership.effectiveAccessEndsOn` (`YYYY-MM-DD`), or null (DNKAPI-0600). */
  effectiveAccessEndsOn: string | null;
  /**
   * Nested `membership.paymentIssue` (DNKAPI-0704): the subscription's latest payment failed
   * and nothing has cleared it yet. Only an explicit `true` is an issue; absent (an older
   * server) or odd-typed reads `false`.
   */
  paymentIssue: boolean;
  /** Nested `membership.paymentIssueSince` (ISO instant of the first failure), or null. */
  paymentIssueSince: string | null;
  /**
   * Per-bucket balances, or null when the wire omitted them (no ledger account) —
   * distinct from an empty array (a known account with no positive buckets).
   */
  creditBalances: CreditBalance[] | null;
  /** The contact's profile-photo path/URL, or null (the initials fallback). */
  photoUrl: string | null;
  /**
   * The member's EFFECTIVE home location (DNKAPI-0329; DNKADM-0239) — the stored
   * home, or the server's read-time single-active-location fallback; `null` when
   * unset in a multi-active-location tenant. Drives which location's tax rates
   * apply to the member's taxable purchases; while `null`, a taxable purchase
   * defers to "tax calculated at checkout" in the member app.
   */
  homeLocation: MemberHomeLocation | null;
  /** The member's assigned coach, embedded by the member-detail read. */
  coach: MemberCoach;
  /**
   * Whether the member has confirmed their email (DNKAPI-0569). `null` means unknown
   * (absent/odd-typed, unresolved contact, or an upstream predating the field) and
   * must never be shown as unconfirmed.
   */
  emailVerified: boolean | null;
}

/**
 * A member's home location (DNKAPI-0329) — the resolved `v2_location.id`
 * (normalized to a string, DNKADM-0064) + its live-resolved display name. `name`
 * may be null when the location is no longer resolvable; `locationId` may be null
 * on a malformed wire value (the caller falls back to whichever is present).
 */
export interface MemberHomeLocation {
  locationId: string | null;
  name: string | null;
}

// --- Defensive parsing helpers -------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive as
 * numbers on the wire and are normalized to the `string` the UI keys off — a finite
 * number → `String(n)`, a non-empty string → trimmed, else null. Used ONLY for nested
 * entity-reference fk ids (`creditBucketId`/`bucketId`, `membershipPlanId`/`planId`,
 * `staffId`). The member's OWN identity (`contactId`/`id`) is a genuine external GHL
 * string id and stays {@link asText} — never coerced.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized oddly), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function parseSummary(raw: unknown): MemberSummary {
  const o = isObject(raw) ? raw : {};
  return {
    id: asText(o['id']) ?? asText(o['contactId']),
    name: asText(o['name']),
    email: asText(o['email']),
    membershipTier: asText(o['membershipTier']) ?? asText(o['membershipPlanName']),
    membershipStatus: asText(o['membershipStatus']),
    subscriptionStatus: asText(o['subscriptionStatus']),
    cancelScheduledAt: asText(o['cancelScheduledAt']),
    effectiveAccessEndsOn: asText(o['effectiveAccessEndsOn']),
    bayCredits: asNumber(o['bayCredits']),
    lastVisit: asText(o['lastVisit']),
  };
}

/**
 * Parse one raw per-bucket balance. The bucket id is the key; a row carrying NEITHER
 * an id nor a name is dropped by the caller (it can't be operated on or labeled).
 * Accepts the v2 flat shape (`creditBucketId`/`bucketName`) and tolerates a nested
 * `bucket: { id, name }` shape.
 */
function parseCreditBalance(raw: unknown): CreditBalance | null {
  if (!isObject(raw)) return null;
  const bucket = isObject(raw['bucket']) ? raw['bucket'] : {};
  // Nested credit-bucket fk id — numeric on the wire (DNKADM-0064).
  const creditBucketId = asId(raw['creditBucketId']) ?? asId(raw['bucketId']) ?? asId(bucket['id']);
  const bucketName = asText(raw['bucketName']) ?? asText(raw['name']) ?? asText(bucket['name']);
  // A balance row with no usable id AND no name is not renderable — drop it.
  if (creditBucketId === null && bucketName === null) return null;
  return {
    creditBucketId,
    bucketName,
    balance: asNumber(raw['balance']) ?? 0,
  };
}

function parseCreditSummaryHint(raw: unknown): CreditSummaryHint | null {
  if (!isObject(raw)) return null;
  const amount = asNumber(raw['amount']);
  const date = asText(raw['date']);
  if (amount === null || date === null) return null;
  return { amount, date };
}

/**
 * Parse one summary bucket defensively. The bucket id is required because U2 keys
 * the summary onto the existing balances by id; malformed/unkeyed rows are
 * dropped rather than guessed.
 */
function parseCreditSummaryEntry(raw: unknown): CreditSummaryEntry | null {
  if (!isObject(raw)) return null;
  const bucket = isObject(raw['bucket']) ? raw['bucket'] : {};
  const bucketId = asId(raw['bucketId']) ?? asId(raw['creditBucketId']) ?? asId(bucket['id']);
  if (bucketId === null) return null;
  return {
    bucketId,
    bucketName: asText(raw['bucketName']) ?? asText(raw['name']) ?? asText(bucket['name']),
    available: asNumber(raw['available']) ?? asNumber(raw['balance']) ?? 0,
    expiringSoon: parseCreditSummaryHint(raw['expiringSoon']),
    arrivingSoon: parseCreditSummaryHint(raw['arrivingSoon']),
  };
}

/**
 * Parse the member's home-location view defensively (DNKADM-0239). The wire shape
 * is `{ locationId, name }` or `null`/absent (no resolvable home). A present view
 * carrying NEITHER a usable id NOR a name degrades to `null` (nothing renderable).
 */
function parseHomeLocation(raw: unknown): MemberHomeLocation | null {
  if (!isObject(raw)) return null;
  // Nested v2 location fk id — numeric on the wire (DNKADM-0064).
  const locationId = asId(raw['locationId']);
  const name = asText(raw['name']);
  if (locationId === null && name === null) return null;
  return { locationId, name };
}

/** Parse the members array defensively: a non-array (or missing) field yields `[]`. */
function parseMembers(raw: unknown): MemberSummary[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseSummary);
}

/**
 * Tolerantly extract the per-bucket balances. Accepts a bare array, or a
 * `creditBalances`/`balances`/`content` wrapper. `null`/absent → `null` (no ledger
 * account), kept distinct from an empty array.
 */
function parseCreditBalances(raw: unknown): CreditBalance[] | null {
  let list: unknown[] | null = null;
  if (Array.isArray(raw)) list = raw;
  else if (isObject(raw)) {
    if (Array.isArray(raw['creditBalances'])) list = raw['creditBalances'];
    else if (Array.isArray(raw['balances'])) list = raw['balances'];
    else if (Array.isArray(raw['content'])) list = raw['content'];
  }
  if (list === null) return null;
  return list.map(parseCreditBalance).filter((b): b is CreditBalance => b !== null);
}

/**
 * Tolerantly extract summary buckets. Accepts the forward bare array, expected
 * `{ buckets: [...] }` envelope, and common grouping/page wrappers so backend
 * grouping changes degrade to "parse what is present".
 */
function parseCreditSummary(raw: unknown): CreditSummaryEntry[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (isObject(raw)) {
    if (Array.isArray(raw['buckets'])) list = raw['buckets'];
    else if (Array.isArray(raw['creditSummary'])) list = raw['creditSummary'];
    else if (Array.isArray(raw['summaries'])) list = raw['summaries'];
    else if (Array.isArray(raw['content'])) list = raw['content'];
  }
  return list.map(parseCreditSummaryEntry).filter((e): e is CreditSummaryEntry => e !== null);
}

// --- List + detail (U1) --------------------------------------------------------

export interface ListMembersParams {
  page?: number;
  size?: number;
  /** Location-wide member search term — sent only when non-empty (trimmed). */
  search?: string;
}

/**
 * `GET /v2/admin/members?page&size&search` → a parsed page of member summaries.
 * Defaults `page=1`, `size=20`. The `search` term is forwarded only when non-empty.
 * Tolerantly reads the §10 envelope (`{ content, page, size, total }`) — also
 * accepting a `members` wrapper or a bare array — so a different live shape degrades
 * rather than throws. Throws `ApiError` on a non-2xx response.
 */
export async function listMembers(params: ListMembersParams = {}): Promise<MembersPage> {
  const page = params.page ?? 1;
  const size = params.size ?? 20;
  const search = params.search?.trim();
  // The UI keeps a 1-indexed page, but the dink-api `/v2/admin/members` endpoint is
  // 0-indexed (`from = pageIndex * pageSize`). Send `toWirePage(page)` on the wire so
  // the first UI page (1) maps to `?page=0` — sending `?page=1` skipped past the first
  // page and surfaced an empty list with a non-zero total. (One canonical conversion —
  // src/api/paging.ts, DNKADM-0148.)
  const body = await request<unknown>('GET', PATH, {
    query: { page: toWirePage(page), size, search: search ? search : undefined },
  });
  const o = isObject(body) ? body : {};
  // §10 envelope: `content` is the page array; tolerate a v1-style `members` wrapper
  // or a bare array (degrade, don't throw).
  const rawList = Array.isArray(body)
    ? body
    : Array.isArray(o['content'])
      ? o['content']
      : o['members'];
  // Map the 0-indexed page in the response back to the 1-indexed value the UI expects
  // (so next/prev paging and "Page X" stay correct); fall back to the requested page.
  const backendPage = asNumber(o['page']);
  return {
    members: parseMembers(rawList),
    page: backendPage !== null ? toUiPage(backendPage) : page,
    size: asNumber(o['size']) ?? asNumber(o['pageLimit']) ?? size,
    total: asNumber(o['total']) ?? asNumber(o['totalElements']),
  };
}

/**
 * `GET /v2/admin/members/{contactId}` → parsed member detail incl. per-bucket credit
 * balances. `contactId` is the external contact-system id (DATA). Throws `ApiError`
 * on a non-2xx response (the caller renders the 403 "needs OWNER/ADMIN" state).
 */
export async function getMember(contactId: string): Promise<MemberDetail> {
  const body = await request<unknown>('GET', `${PATH}/${encodeURIComponent(contactId)}`);
  const o = isObject(body) ? body : {};
  // DNKAPI-0600: the membership state is NESTED (`membership` is a MembershipView); the
  // detail has never sent top-level tier/status. The top-level reads stay as a fallback only.
  const m = isObject(o['membership']) ? o['membership'] : {};
  return {
    id: asText(o['id']) ?? asText(o['contactId']),
    name: asText(o['name']),
    email: asText(o['email']),
    phone: asText(o['phone']),
    joinedAt: asText(o['joinedAt']),
    membershipTier:
      asText(m['tier']) ?? asText(o['membershipTier']) ?? asText(o['membershipPlanName']),
    membershipStatus: asText(m['effectiveStatus']) ?? asText(o['membershipStatus']),
    subscriptionStatus: asText(m['subscriptionStatus']),
    cancelScheduledAt: asText(m['cancelScheduledAt']),
    effectiveAccessEndsOn: asText(m['effectiveAccessEndsOn']),
    paymentIssue: m['paymentIssue'] === true,
    paymentIssueSince: asText(m['paymentIssueSince']),
    creditBalances: parseCreditBalances(o['creditBalances'] ?? o['credits']),
    photoUrl: asText(o['photoUrl']),
    homeLocation: parseHomeLocation(o['homeLocation']),
    coach: parseCoach(o['coach']),
    // DNKADM-0410: only an explicit boolean is a signal; absent/malformed stays null.
    emailVerified: typeof o['emailVerified'] === 'boolean' ? o['emailVerified'] : null,
  };
}

// --- Member erasure (GDPR right-to-be-forgotten — DNKADM-0203) ------------------

/**
 * The three erasure OUTCOMES — all arrive as an HTTP 200 body; dink's OWN member data
 * is already gone in every case (the caller navigates away for all three):
 *  - `completed` — fully erased.
 *  - `partial`   — erased, but a payment-processor card removal is being auto-retried.
 *  - `blocked`   — erased, but a downstream leg (e.g. a period-end subscription cancel)
 *    self-completes in the background; NO admin retry. This is the OPPOSITE of the
 *    `409 ERASURE_BLOCKED_LAST_OWNER` refusal (where NOTHING is erased).
 */
export type MemberErasureStatus = 'completed' | 'partial' | 'blocked';

/**
 * Parsed `POST /v2/admin/members/{contactId}/erasure` success body (DNKAPI-0338/0339).
 * `erasureId` and `report` may be null on the wire. An UNKNOWN/absent `status` on a 200
 * degrades to `'blocked'` — the conservative in-progress framing (data likely gone,
 * self-completes, no admin retry) rather than falsely reporting a full `completed`.
 */
export interface MemberErasureResponse {
  erasureId: string | null;
  status: MemberErasureStatus;
  report: unknown | null;
}

const ERASURE_STATUSES: readonly MemberErasureStatus[] = [
  'completed',
  'partial',
  'blocked',
] as const;

/** Normalize a raw erasure status; anything unknown degrades to the conservative `'blocked'`. */
function asErasureStatus(value: unknown): MemberErasureStatus {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if ((ERASURE_STATUSES as readonly string[]).includes(lower)) {
      return lower as MemberErasureStatus;
    }
  }
  return 'blocked';
}

/**
 * `POST /v2/admin/members/{contactId}/erasure` — IRREVERSIBLE GDPR right-to-be-forgotten
 * erase (hard-deletes the member's login + marketing/CRM contact, photo, and profile;
 * anonymizes-and-retains financial records). **NO request body**; bearer-only
 * `/v2/admin/**` (no CSRF, no cookies). Parses the {@link MemberErasureResponse}
 * defensively (null `erasureId`/`report` tolerated, unknown `status` → `'blocked'`).
 * Throws a typed `ApiError` — `409 ERASURE_BLOCKED_LAST_OWNER` (the member is the tenant's
 * last active owner; NOTHING erased), `502`, `500`, `401/403`.
 */
export async function eraseMember(contactId: string): Promise<MemberErasureResponse> {
  const body = await request<unknown>('POST', `${PATH}/${encodeURIComponent(contactId)}/erasure`);
  const o = isObject(body) ? body : {};
  return {
    // The erasure id is a genuine external id string — asText, never number-coerced.
    erasureId: asText(o['erasureId']),
    status: asErasureStatus(o['status']),
    report: o['report'] ?? null,
  };
}

/** The 200 outcomes of a verification-email resend (DNKAPI-0650). */
export type ResendVerificationOutcome = 'SENT' | 'ALREADY_VERIFIED' | 'NO_ACCOUNT';

/**
 * `POST /v2/admin/members/{contactId}/resend-verification` (DNKAPI-0650) — NO request
 * body; the address is the member's own, resolved server-side. Resolves the `outcome`,
 * or `null` when the 200 carried an unrecognised one (the caller treats that as a
 * failure, never as a success). Throws a typed `ApiError` — `429
 * VERIFICATION_RESEND_RATE_LIMITED`, `409 MEMBER_APP_URL_UNAVAILABLE`, `502
 * VERIFICATION_RESEND_FAILED`, `401/403`.
 */
export async function resendMemberVerification(
  contactId: string,
): Promise<ResendVerificationOutcome | null> {
  const body = await request<unknown>(
    'POST',
    `${PATH}/${encodeURIComponent(contactId)}/resend-verification`,
  );
  const outcome = isObject(body) ? body['outcome'] : undefined;
  return outcome === 'SENT' || outcome === 'ALREADY_VERIFIED' || outcome === 'NO_ACCOUNT'
    ? outcome
    : null;
}

// --- Credits: balances + adjust (U2) -------------------------------------------

/**
 * `GET /v2/admin/members/{contactId}/credits` → the member's per-bucket balances
 * (bucket-ID-keyed). Returns `null` when the wire omitted the field (no ledger
 * account), distinct from `[]`. Throws `ApiError` on a non-2xx response.
 */
export async function getMemberCredits(contactId: string): Promise<CreditBalance[] | null> {
  const body = await request<unknown>('GET', `${PATH}/${encodeURIComponent(contactId)}/credits`);
  return parseCreditBalances(body);
}

/**
 * `GET /v2/admin/members/{contactId}/credit-summary` → per-bucket available
 * balances plus optional expiring/arriving hints. This is a forward-compatible
 * endpoint: a `404` (not deployed / not available yet) resolves to `null`, the
 * caller contract for "no summary available; fall back to plain balances with no
 * hints". Other non-2xx failures still throw a typed `ApiError`.
 */
export async function getCreditSummary(contactId: string): Promise<CreditSummaryEntry[] | null> {
  try {
    const body = await request<unknown>(
      'GET',
      `${PATH}/${encodeURIComponent(contactId)}/credit-summary`,
    );
    return parseCreditSummary(body);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'NOT_FOUND') return null;
    throw err;
  }
}

/**
 * One bucket entry in a credit-grant request (bucket-ID-keyed). `creditBucketId`
 * is the 0052 bucket id; `amount` must be a positive grant. Reductions use
 * {@link reduceBucketCredits} instead of negative adjustment lines.
 */
interface CreditAdjustmentEntry {
  creditBucketId: string;
  /** Positive grant amount. Non-positive values are blocked before the request. */
  amount: number;
  /** ISO-8601 instant (optional, grants only). */
  expiresAt?: string | null;
  /** DNKAPI-0648 plain `YYYY-MM-DD` last usable day, sent INSTEAD of `expiresAt` (grants only). */
  lastUsableDay?: string | null;
}

/**
 * Input for a single-bucket adjust — `adjustCredits(contactId, { creditBucketId,
 * amount, reason })`. A thin convenience over the batch contract.
 */
export interface AdjustCreditsInput {
  creditBucketId: string;
  amount: number;
  reason: string;
  expiresAt?: string | null;
}

/**
 * Input for the multi-bucket batch grant. `reason` is a required, non-blank audit
 * note recorded for every bucket in the batch; `adjustments` carries only the buckets
 * receiving a positive grant, each keyed by `creditBucketId`.
 */
export interface AdjustCreditsBatchInput {
  reason: string;
  adjustments: CreditAdjustmentEntry[];
  /**
   * DNKADM-0479 (F-244): optional retry key (1-128 of A-Z a-z 0-9 . _ : -). The caller keeps
   * ONE key per change it means once and re-sends it unchanged after an unknown outcome;
   * dink-api (DNKAPI-0693) then answers without applying the change again. Sent only when set.
   */
  idempotencyKey?: string;
}

/** Input for bucket-level credit reduction. */
export interface ReduceBucketCreditsInput {
  /** Positive amount to remove from the bucket. Non-positive values are blocked before the request. */
  amount: number;
  /** Optional shared operator reason. Sent only when non-blank. */
  reason?: string | null;
  /**
   * DNKADM-0479 (F-244): optional retry key (1-128 of A-Z a-z 0-9 . _ : -). The caller keeps
   * ONE key per change it means once and re-sends it unchanged after an unknown outcome;
   * dink-api (DNKAPI-0693) then answers without applying the change again. Sent only when set.
   */
  idempotencyKey?: string;
}

/** One result entry from a batch response — the bucket's NEW ledger balance. */
interface CreditAdjustBatchResultEntry {
  creditBucketId: string | null;
  /** The positive grant amount that was applied. */
  amount: number;
  /** The bucket's new ledger balance (the source of truth) after the adjustment. */
  balance: number;
}

/** Parsed `POST /v2/admin/members/{contactId}/credits/adjust` batch result. */
export interface CreditAdjustBatchResult {
  adjustmentBatchId: string | null;
  results: CreditAdjustBatchResultEntry[];
}

function assertPositiveAmount(amount: number, operation: string): void {
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new ApiError('VALIDATION', 400, {
      details: { message: `${operation} amount must be greater than zero.` },
    });
  }
}

/**
 * Build one grant-entry body (`expiresAt` only on a positive grant). The bucket id
 * is serialized under `bucketId` as a numeric `Long` — the key the shipped
 * `/credits/adjust` endpoint binds (the UI's string id is coerced to a number).
 */
function toAdjustmentBody(entry: CreditAdjustmentEntry): Record<string, unknown> {
  assertPositiveAmount(entry.amount, 'Credit adjustment');
  const a: Record<string, unknown> = {
    bucketId: Number(entry.creditBucketId),
    amount: entry.amount,
  };
  if (entry.lastUsableDay != null) a[LOT_DAY_WIRE.write] = entry.lastUsableDay;
  else if (entry.expiresAt != null) a['expiresAt'] = entry.expiresAt;
  return a;
}

function parseAdjustResult(body: unknown): CreditAdjustBatchResult {
  const o = isObject(body) ? body : {};
  const rawResults = Array.isArray(body)
    ? (body as unknown[])
    : Array.isArray(o['results'])
      ? (o['results'] as unknown[])
      : [];
  const results: CreditAdjustBatchResultEntry[] = rawResults.map((r) => {
    const row = isObject(r) ? r : {};
    return {
      creditBucketId: asId(row['bucketId']) ?? asId(row['creditBucketId']),
      amount: asNumber(row['amount']) ?? 0,
      balance: asNumber(row['balance']) ?? 0,
    };
  });
  return { adjustmentBatchId: asText(o['adjustmentBatchId']), results };
}

/**
 * `POST /v2/admin/members/{contactId}/credits/adjust` (multi-bucket) — grant a
 * member credits across one or more buckets in a single atomic call, each keyed on
 * the wire by **`bucketId`** (numeric `Long`). Bearer-only `/v2/admin/**` surface (no
 * CSRF, no cookies). The request carries `{ reason, adjustments: [{ bucketId, amount,
 * expiresAt? }] }`; `amount` must be positive and `expiresAt` is optional.
 * All-or-nothing: a `4xx` means no grant lots were created (`400` VALIDATION, `403`
 * FORBIDDEN). Responds with the post-adjust bucket balances; throws a typed
 * `ApiError` and parses the response defensively.
 */
export async function adjustMemberCredits(
  contactId: string,
  input: AdjustCreditsBatchInput,
): Promise<CreditAdjustBatchResult> {
  const body = await request<unknown>(
    'POST',
    `${PATH}/${encodeURIComponent(contactId)}/credits/adjust`,
    {
      json: {
        reason: input.reason,
        adjustments: input.adjustments.map(toAdjustmentBody),
        ...(input.idempotencyKey !== undefined ? { idempotencyKey: input.idempotencyKey } : {}),
      },
    },
  );
  return parseAdjustResult(body);
}

/**
 * `adjustCredits(contactId, { creditBucketId, amount, reason })` — the single-bucket
 * convenience over {@link adjustMemberCredits}. Wraps the one bucket into the batch
 * grant contract. Throws a typed `ApiError`.
 */
export async function adjustCredits(
  contactId: string,
  input: AdjustCreditsInput,
): Promise<CreditAdjustBatchResult> {
  const entry: CreditAdjustmentEntry = {
    creditBucketId: input.creditBucketId,
    amount: input.amount,
  };
  if (input.expiresAt != null) entry.expiresAt = input.expiresAt;
  return adjustMemberCredits(contactId, { reason: input.reason, adjustments: [entry] });
}

/**
 * `PUT /v2/admin/members/{contactId}/credits/{bucketId}/reduce` — remove credits
 * from one bucket by a positive amount using the server's bucket-scoped FIFO debit.
 * `reason` is optional and sent only when non-blank. Throws a typed `ApiError`;
 * `400` maps to `VALIDATION` and overdraw maps to `409`/`INSUFFICIENT_CREDITS`.
 */
export async function reduceBucketCredits(
  contactId: string,
  bucketId: string,
  input: ReduceBucketCreditsInput,
): Promise<void> {
  assertPositiveAmount(input.amount, 'Credit reduction');
  const json: Record<string, unknown> = { amount: input.amount };
  const reason = input.reason?.trim();
  if (reason) json['reason'] = reason;
  if (input.idempotencyKey !== undefined) json['idempotencyKey'] = input.idempotencyKey;
  await request<unknown>(
    'PUT',
    `${PATH}/${encodeURIComponent(contactId)}/credits/${encodeURIComponent(bucketId)}/reduce`,
    { json },
  );
}

// --- Membership (U3) -----------------------------------------------------------

/**
 * Parsed `GET /v2/admin/members/{contactId}/membership` — the member's current
 * membership. `membershipPlanId` is the 0056 plan id (the plan IS the tier).
 * `membershipStatus`/`effectiveState` carry the server's `effectiveStatus` — the
 * ENTITLEMENT answer, never re-derived client-side. It is what the summary bar's
 * chip and Manage action set key off (dink-api ruling 2026-08-03, DNKADM-0242);
 * `subscriptionStatus` only ENRICHES the display. `storedStatus` preserves the
 * persisted status when the server returns it.
 */
export interface MemberMembership {
  /** Live subscription id for this member's current membership, or null when absent. */
  subscriptionId: string | null;
  membershipPlanId: string | null;
  /** Plan display name (server-resolved), or null. */
  membershipPlanName: string | null;
  membershipStatus: string | null;
  storedStatus: string | null;
  /** ISO date (`YYYY-MM-DD`) or instant, or null. */
  membershipExpiration: string | null;
  /** Server-derived effective state (e.g. `active`/`grace`/`expired`) — read-only. */
  effectiveState: string | null;
  subscriptionStatus: string | null;
  pausedAt: string | null;
  /**
   * Non-null when an at-period-end cancel is already pending (DNKAPI-0397) — a
   * SEPARATE field, not a `subscriptionStatus` value: the subscription is genuinely
   * still `active` until the period boundary. Absent on a backend that predates
   * DNKAPI-0397, which parses to `null` (today's behavior).
   */
  cancelScheduledAt: string | null;
  /**
   * The member's last day of access, `YYYY-MM-DD` (DNKAPI-0600): expiration plus the
   * tenant's grace days. Null with no membership or no expiration, and on a backend that
   * predates the field. Display-only, rendered as sent.
   */
  effectiveAccessEndsOn: string | null;
  /**
   * The subscription's latest payment failed and no later success or card update has
   * cleared it (DNKAPI-0704). Shown whatever the tenant's block setting says. Only an
   * explicit `true` counts; absent (an older server) or odd-typed reads `false`.
   */
  paymentIssue: boolean;
  /** When the failure was first recorded (ISO instant), or null with no payment issue. */
  paymentIssueSince: string | null;
  maskedCard: string | null;
}

function parseMembership(body: unknown): MemberMembership {
  const o = isObject(body) ? body : {};
  const effectiveStatus = asText(o['effectiveStatus']);
  return {
    subscriptionId: asText(o['subscriptionId']),
    membershipPlanId: asId(o['membershipPlanId']) ?? asId(o['planId']),
    membershipPlanName:
      asText(o['tier']) ?? asText(o['membershipPlanName']) ?? asText(o['planName']),
    membershipStatus: effectiveStatus ?? asText(o['membershipStatus']) ?? asText(o['status']),
    storedStatus: asText(o['storedStatus']),
    membershipExpiration: asText(o['membershipExpiration']) ?? asText(o['expiration']),
    effectiveState:
      effectiveStatus ??
      asText(o['effectiveState']) ??
      asText(o['effective']) ??
      asText(o['status']),
    subscriptionStatus: asText(o['subscriptionStatus']),
    pausedAt: asText(o['pausedAt']),
    cancelScheduledAt: asText(o['cancelScheduledAt']),
    effectiveAccessEndsOn: asText(o['effectiveAccessEndsOn']),
    paymentIssue: o['paymentIssue'] === true,
    paymentIssueSince: asText(o['paymentIssueSince']),
    maskedCard: asText(o['maskedCard']),
  };
}

/**
 * `GET /v2/admin/members/{contactId}/membership` → the member's membership
 * (defensively parsed).
 *
 * A `404` resolves to the ABSENT membership — the same all-null view this endpoint
 * already returns with `200` when there is no membership row (DNKADM-0333). dink-api's
 * member-recognition test can report a roster-only member (a claimed invitation with no
 * membership, credit account, staff row, or profile value) not-found; DNKAPI-0494 added
 * the platform roster as a fifth recognition source but FAILS OPEN, so a saas-auth outage
 * still 404s. On a screen reached FROM the member roster, "not a member of this tenant"
 * and "no membership" are the same empty card, so collapsing them is truthful — and it is
 * what keeps `Buy membership` offered for a member who genuinely has none.
 *
 * Every OTHER failure still throws a typed `ApiError`: a failed load must never be
 * indistinguishable from an empty result.
 */
export async function getMemberMembership(contactId: string): Promise<MemberMembership> {
  try {
    const body = await request<unknown>(
      'GET',
      `${PATH}/${encodeURIComponent(contactId)}/membership`,
    );
    return parseMembership(body);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'NOT_FOUND') return parseMembership({});
    throw err;
  }
}

// --- Subscription pre-flight + relink self-heal (DNKADM-0226) ------------------

const SUBSCRIPTION_PREFLIGHT_FIELDS = {
  decision: 'decision',
  subscriptionId: 'subscriptionId',
} as const;

const SUBSCRIPTION_RELINK_PREVIEW_FIELDS = {
  subscriptionId: 'subscriptionId',
  status: 'status',
  tier: 'tier',
  planName: 'planName',
  membershipPlanName: 'membershipPlanName',
  pspSubscriptionId: 'pspSubscriptionId',
  createdAt: 'createdAt',
  blocker: 'blocker',
  blockerCode: 'blockerCode',
} as const;

/**
 * The plan-scoped decision set (dink-api B1, 2026-08-03): the four plan-agnostic
 * values PLUS `HAS_ACTIVE_MEMBERSHIP`, which only this mode can answer — "a live
 * subscription on ANOTHER plan", i.e. a BLOCK, not a manage-able membership.
 * `UNKNOWN` is the local fail-closed guard, never a wire value.
 */
type SubscriptionPreflightDecision =
  | 'PROCEED'
  | 'ALREADY_ACTIVE'
  | 'HAS_ACTIVE_MEMBERSHIP'
  | 'ORPHAN_RELINK'
  | 'IN_PROGRESS'
  | 'UNKNOWN';

export interface SubscriptionPreflight {
  decision: SubscriptionPreflightDecision;
  subscriptionId: string | null;
}

export type SubscriptionRelinkBlocker = 'canceled' | 'creating' | 'wrong-member';

export interface SubscriptionRelinkPreview {
  subscriptionId: string | null;
  status: string | null;
  tier: string | null;
  pspSubscriptionId: string | null;
  createdAt: string | null;
  blocker: SubscriptionRelinkBlocker | null;
}

function parsePreflightDecision(value: unknown): SubscriptionPreflightDecision {
  const raw = asText(value)?.toUpperCase();
  if (
    raw === 'PROCEED' ||
    raw === 'ALREADY_ACTIVE' ||
    raw === 'HAS_ACTIVE_MEMBERSHIP' ||
    raw === 'ORPHAN_RELINK' ||
    raw === 'IN_PROGRESS'
  ) {
    return raw;
  }
  return 'UNKNOWN';
}

function parseSubscriptionRelinkBlocker(value: unknown): SubscriptionRelinkBlocker | null {
  const normalized = asText(value)?.toLowerCase().replace(/_/g, '-');
  if (normalized === 'canceled') return 'canceled';
  if (normalized === 'creating') return 'creating';
  if (normalized === 'wrong-member' || normalized === 'wrong_member') return 'wrong-member';
  return null;
}

function parseSubscriptionPreflight(body: unknown): SubscriptionPreflight {
  const o = isObject(body) ? body : {};
  return {
    decision: parsePreflightDecision(o[SUBSCRIPTION_PREFLIGHT_FIELDS.decision]),
    subscriptionId: asText(o[SUBSCRIPTION_PREFLIGHT_FIELDS.subscriptionId]),
  };
}

function parseSubscriptionRelinkPreview(body: unknown): SubscriptionRelinkPreview {
  const o = isObject(body) ? body : {};
  return {
    subscriptionId: asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.subscriptionId]),
    status: asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.status]),
    tier:
      asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.tier]) ??
      asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.planName]) ??
      asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.membershipPlanName]),
    pspSubscriptionId: asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.pspSubscriptionId]),
    createdAt: asText(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.createdAt]),
    blocker:
      parseSubscriptionRelinkBlocker(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.blocker]) ??
      parseSubscriptionRelinkBlocker(o[SUBSCRIPTION_RELINK_PREVIEW_FIELDS.blockerCode]),
  };
}

export async function getSubscriptionPreflight(
  contactId: string,
  planId: string | number,
): Promise<SubscriptionPreflight> {
  const body = await request<unknown>(
    'GET',
    `${PATH}/${encodeURIComponent(contactId)}/membership/subscription-preflight`,
    { query: { planId } },
  );
  return parseSubscriptionPreflight(body);
}

// --- Plan-agnostic membership ENTRY pre-flight (DNKADM-0232) -------------------
//
// SHIPPED CONTRACT. The same `subscription-preflight` path answers a SECOND,
// plan-agnostic mode when `planId` is omitted: "does this member already have a
// subscription at all?", asked the moment the operator starts a purchase — before
// any plan is chosen. dink-api B2 (2026-08-03) confirms the decision vocabulary is
// SETTLED and append-only, so this is no longer a forward contract — but the mode
// answers with the plan-scoped wire tokens (DNKADM-0235), so the token table below
// maps those onto this mode's own decisions.
//
// Every wire string the mode depends on (the path suffix, the field names, the
// decision tokens) still lives in the three constants below, so a future appended
// token is a one-line edit there and nothing else moves.

/**
 * Path suffix of the entry pre-flight. Same endpoint the plan-scoped call above
 * spells inline; isolated here (not shared) so the two modes stay independently
 * adjustable and the plan-agnostic one can never disturb the plan-scoped gate.
 */
const MEMBERSHIP_ENTRY_PREFLIGHT_PATH = 'membership/subscription-preflight';

const MEMBERSHIP_ENTRY_PREFLIGHT_FIELDS = {
  decision: 'decision',
  subscriptionId: 'subscriptionId',
  planId: 'planId',
  tier: 'tier',
} as const;

/**
 * The wire tokens for the plan-agnostic decision (upper-cased before matching),
 * grouped by the entry decision each maps to.
 *
 * These are the tokens the shipped backend actually answers with (dink-api B1,
 * 2026-08-03): the plan-agnostic mode returns `PROCEED · ALREADY_ACTIVE ·
 * ORPHAN_RELINK · IN_PROGRESS`. The `NONE`/`LINKED`/`ORPHAN` names from the
 * 2026-08-01 handoff were placeholders that never shipped in any build, so the
 * aliases DNKADM-0235 added for them are gone (DNKADM-0242).
 *
 * `HAS_ACTIVE_MEMBERSHIP` stays here even though only the PLAN-SCOPED mode can
 * return it: unreachable in plan-agnostic mode by construction, and if it ever did
 * arrive, LINKED (route to Manage) is the safe reading. On the plan-scoped gate the
 * SAME token means something different — a BLOCK, "a live subscription on another
 * plan" — which is why the two tables are separate and must NOT be merged.
 *
 * This is safe in the only direction that matters: every token implying a LIVE
 * subscription maps to a decision that routes AWAY from Buy (re-link / Manage /
 * retry); only `PROCEED` is "clear to buy".
 */
const MEMBERSHIP_ENTRY_PREFLIGHT_DECISIONS: Record<
  'none' | 'linked' | 'orphan' | 'inProgress',
  readonly string[]
> = {
  none: ['PROCEED'],
  linked: ['ALREADY_ACTIVE', 'HAS_ACTIVE_MEMBERSHIP'],
  orphan: ['ORPHAN_RELINK'],
  inProgress: ['IN_PROGRESS'],
};

/**
 * `UNKNOWN` is the local fail-closed guard, not a wire value: an absent or
 * unrecognized decision must NEVER be read as "no subscription", because that would
 * open the Buy flow on a member who may already have one. dink-api B2 settled the
 * vocabulary as append-only, so this default is PERMANENT — a future appended token
 * fails closed here until it is added to the table above.
 */
type MembershipEntryPreflightDecision = 'NONE' | 'LINKED' | 'ORPHAN' | 'IN_PROGRESS' | 'UNKNOWN';

export interface MembershipEntryPreflight {
  decision: MembershipEntryPreflightDecision;
  /** Present for LINKED / ORPHAN / IN_PROGRESS. */
  subscriptionId: string | null;
  /** The plan the existing subscription is on (LINKED / ORPHAN). */
  planId: string | null;
  /** That plan's display name — backend text, scrub at the render boundary. */
  tier: string | null;
}

function parseMembershipEntryPreflightDecision(value: unknown): MembershipEntryPreflightDecision {
  const raw = asText(value)?.toUpperCase();
  if (raw === undefined) return 'UNKNOWN';
  if (MEMBERSHIP_ENTRY_PREFLIGHT_DECISIONS.none.includes(raw)) return 'NONE';
  if (MEMBERSHIP_ENTRY_PREFLIGHT_DECISIONS.linked.includes(raw)) return 'LINKED';
  if (MEMBERSHIP_ENTRY_PREFLIGHT_DECISIONS.orphan.includes(raw)) return 'ORPHAN';
  if (MEMBERSHIP_ENTRY_PREFLIGHT_DECISIONS.inProgress.includes(raw)) return 'IN_PROGRESS';
  return 'UNKNOWN';
}

function parseMembershipEntryPreflight(body: unknown): MembershipEntryPreflight {
  const o = isObject(body) ? body : {};
  return {
    decision: parseMembershipEntryPreflightDecision(o[MEMBERSHIP_ENTRY_PREFLIGHT_FIELDS.decision]),
    subscriptionId: asText(o[MEMBERSHIP_ENTRY_PREFLIGHT_FIELDS.subscriptionId]),
    planId: asId(o[MEMBERSHIP_ENTRY_PREFLIGHT_FIELDS.planId]),
    tier: asText(o[MEMBERSHIP_ENTRY_PREFLIGHT_FIELDS.tier]),
  };
}

/**
 * `GET /v2/admin/members/{contactId}/membership/subscription-preflight` with **no
 * `planId`** — the plan-agnostic entry gate for "Buy membership". Only `NONE` may
 * open the buy flow; everything else (including an unparseable response) routes the
 * operator elsewhere.
 */
export async function getMembershipEntryPreflight(
  contactId: string,
): Promise<MembershipEntryPreflight> {
  const body = await request<unknown>(
    'GET',
    `${PATH}/${encodeURIComponent(contactId)}/${MEMBERSHIP_ENTRY_PREFLIGHT_PATH}`,
  );
  return parseMembershipEntryPreflight(body);
}

export async function getSubscriptionRelinkPreview(
  contactId: string,
  subscriptionId: string,
): Promise<SubscriptionRelinkPreview> {
  const body = await request<unknown>(
    'GET',
    `${PATH}/${encodeURIComponent(contactId)}/membership/subscription-relink-preview`,
    { query: { subscriptionId } },
  );
  return parseSubscriptionRelinkPreview(body);
}

export async function linkSubscription(
  contactId: string,
  subscriptionId: string,
): Promise<MemberMembership> {
  const body = await request<unknown>(
    'POST',
    `${PATH}/${encodeURIComponent(contactId)}/membership/link-subscription`,
    { json: { subscriptionId, confirm: true } },
  );
  return parseMembership(body);
}

// --- Coach (U3) ----------------------------------------------------------------

/**
 * The member's assigned coach (§5.14) — a **`v2_staff.id`** (the 0053 entity), NOT a
 * contact-system user id. `staffName` is the server-resolved display name.
 */
export interface MemberCoach {
  /** The assigned `v2_staff.id`, or null when unassigned. */
  staffId: string | null;
  /** Staff display name (server-resolved), or null. */
  staffName: string | null;
}

function parseCoach(body: unknown): MemberCoach {
  const o = isObject(body) ? body : {};
  const staff = isObject(o['staff']) ? o['staff'] : {};
  // Tolerate a `{ firstName, lastName }` staff shape for the display name.
  const composed = [asText(staff['firstName']), asText(staff['lastName'])]
    .filter((p): p is string => p !== null)
    .join(' ');
  return {
    staffId: asId(o['staffId']) ?? asId(staff['id']),
    staffName:
      asText(o['name']) ??
      asText(o['staffName']) ??
      asText(staff['name']) ??
      (composed.length > 0 ? composed : null),
  };
}

/**
 * `GET /v2/admin/members/{contactId}/staff` → the member's assigned coach
 * (`v2_staff.id`), defensively parsed. Throws a typed `ApiError`.
 */
export async function getMemberCoach(contactId: string): Promise<MemberCoach> {
  const body = await request<unknown>('GET', `${PATH}/${encodeURIComponent(contactId)}/staff`);
  return parseCoach(body);
}

/**
 * `PUT /v2/admin/members/{contactId}/staff` ← `{ staffId }` → assign/change the
 * member's coach. `staffId` is a **`v2_staff.id`** (the 0053 pick-list), NOT a
 * contact-system user id. Bearer-only `/v2/admin/**`. Throws a typed `ApiError`.
 */
export async function setMemberCoach(contactId: string, staffId: string): Promise<MemberCoach> {
  const numericStaffId = Number(staffId);
  if (
    staffId.trim().length === 0 ||
    !Number.isFinite(numericStaffId) ||
    !Number.isInteger(numericStaffId)
  ) {
    throw new TypeError('staffId must be a finite integer');
  }
  const body = await request<unknown>('PUT', `${PATH}/${encodeURIComponent(contactId)}/staff`, {
    json: { staffId: numericStaffId },
  });
  return parseCoach(body);
}

/**
 * `DELETE /v2/admin/members/{contactId}/staff` → clear the member's coach (idempotent).
 * Returns the (now-empty) coach state. Throws a typed `ApiError`.
 */
export async function clearMemberCoach(contactId: string): Promise<MemberCoach> {
  const body = await request<unknown>('DELETE', `${PATH}/${encodeURIComponent(contactId)}/staff`);
  return parseCoach(body);
}

// --- Home location (DNKADM-0239; DNKAPI-0329) ----------------------------------

/**
 * `PUT /v2/admin/members/{contactId}/home-location` ← `{ locationId }` → set/change
 * the member's home location. `locationId` is a **tenant-scoped, active
 * `v2_location.id`** (the server rejects anything else with `400
 * HOME_LOCATION_INVALID` and performs no upsert). Admin set is always allowed —
 * the sticky "never silently overwritten" rule applies only to the automatic
 * establish-on-purchase path. Server boundary: changing a home does NOT re-tax /
 * auto-migrate an already-active taxed subscription. Returns the server's
 * authoritative `{ locationId, name }` view, defensively parsed. Bearer-only
 * `/v2/admin/**`. Throws a typed `ApiError`.
 */
export async function setMemberHomeLocation(
  contactId: string,
  locationId: string,
): Promise<MemberHomeLocation | null> {
  const body = await request<unknown>(
    'PUT',
    `${PATH}/${encodeURIComponent(contactId)}/home-location`,
    { json: { locationId } },
  );
  return parseHomeLocation(body);
}

// --- Profile values (U3; enriched read — DNKAPI-0195) --------------------------

/**
 * The normalized profile-field data type (DNKAPI-0195). Stable across upstream
 * variance — an unknown/absent upstream type degrades to `TEXT` (the safe default).
 * There is deliberately no boolean member here: the enriched read renders per these
 * five types, and a boolean-ish value is only ever surfaced read-only.
 */
type ProfileFieldType = 'TEXT' | 'SINGLE_OPTIONS' | 'MULTI_OPTIONS' | 'NUMBER' | 'DATE';

/** All profile field types (used for the defensive type normalization). */
const PROFILE_FIELD_TYPES: readonly ProfileFieldType[] = [
  'TEXT',
  'SINGLE_OPTIONS',
  'MULTI_OPTIONS',
  'NUMBER',
  'DATE',
] as const;

/**
 * One enriched profile-field value (DNKAPI-0195 — the BREAKING read shape). Every field
 * is now an OBJECT (no bare-string values):
 *  - `value` — the current value: string | number | boolean | list | `null` (unset).
 *    A list only for `MULTI_OPTIONS`.
 *  - `type` — the normalized input type (drives rendering; supersedes the registry
 *    `fieldType`).
 *  - `options` — the allowed values, present ONLY for `SINGLE_OPTIONS`/`MULTI_OPTIONS`
 *    (omitted otherwise — `exactOptionalPropertyTypes`).
 *  - `writable` — whether the admin may edit it. Parsed FAIL-SAFE: a missing/non-boolean
 *    flag degrades to `false` (read-only) — a field is NEVER wrongly made editable.
 */
export interface ProfileFieldValue {
  value: string | number | boolean | string[] | null;
  type: ProfileFieldType;
  options?: string[];
  writable: boolean;
}

/**
 * The member's enriched profile values (DNKAPI-0195), keyed by the registry `fieldKey`
 * (0058). Each entry is a {@link ProfileFieldValue} carrying its own type/options/
 * writable — the authoritative per-field read that drives the editor's inputs.
 */
export type ProfileValues = Record<string, ProfileFieldValue>;

/**
 * A single bare value on the FLAT write body (DNKAPI-0137 — unchanged). Only the value
 * is submitted (the server owns type/options); NEVER the enriched object.
 */
export type ProfileValueInput = string | number | boolean | string[] | null;

/** The FLAT write body's `values` map — bare values keyed by `fieldKey` (0137). */
export type ProfileValuesInput = Record<string, ProfileValueInput>;

/** Normalize a raw field type; anything unknown degrades to the safe `TEXT` default. */
function asProfileFieldType(value: unknown): ProfileFieldType {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if ((PROFILE_FIELD_TYPES as readonly string[]).includes(upper)) {
      return upper as ProfileFieldType;
    }
  }
  return 'TEXT';
}

/** A boolean from a real boolean, else the fallback (missing/non-boolean → fallback). */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** The string members of a raw array (drops non-strings), or `[]` when not an array. */
function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/** Coerce a raw value to the enriched value union (string | number | boolean | list | null). */
function parseProfileValue(raw: unknown): string | number | boolean | string[] | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return raw;
  }
  if (Array.isArray(raw)) return asStringList(raw);
  // An unexpected object shape degrades to unset rather than leaking through.
  return null;
}

/**
 * Parse one enriched field entry DEFENSIVELY (DNKAPI-0195). A non-object (e.g. a legacy
 * bare value) or a malformed entry degrades to a read-only empty `TEXT` field —
 * fail-safe: `type` missing → `TEXT`, `writable` missing/non-boolean → `false`. `options`
 * is assigned only for the two option types (omitted otherwise).
 */
function parseProfileFieldValue(raw: unknown): ProfileFieldValue {
  const o = isObject(raw) ? raw : {};
  const type = asProfileFieldType(o['type']);
  const field: ProfileFieldValue = {
    value: parseProfileValue(o['value']),
    type,
    writable: asBoolean(o['writable'], false),
  };
  if (type === 'SINGLE_OPTIONS' || type === 'MULTI_OPTIONS') {
    field.options = asStringList(o['options']);
  }
  return field;
}

/** Parse the raw enriched profile-values map — every entry becomes a {@link ProfileFieldValue}. */
function parseProfileValues(body: unknown): ProfileValues {
  // Accept the flat map, or a `{ values: {...} }` wrapper.
  const o = isObject(body) ? body : {};
  const raw = isObject(o['values']) ? o['values'] : o;
  const out: ProfileValues = {};
  for (const [key, value] of Object.entries(raw)) {
    out[key] = parseProfileFieldValue(value);
  }
  return out;
}

/**
 * `GET /v2/admin/members/{contactId}/profile-values` → the member's ENRICHED profile
 * values (DNKAPI-0195 — the breaking shape), keyed by registry `fieldKey`. Each entry
 * carries `{ value, type, options?, writable }`, parsed defensively (unknown type→TEXT,
 * missing writable→read-only). Throws a typed `ApiError`.
 */
export async function getMemberProfileValues(contactId: string): Promise<ProfileValues> {
  const body = await request<unknown>(
    'GET',
    `${PATH}/${encodeURIComponent(contactId)}/profile-values`,
  );
  return parseProfileValues(body);
}

/**
 * `PUT /v2/admin/members/{contactId}/profile-values` ← `{ values }` → write the member's
 * profile values. The body stays **FLAT** (DNKAPI-0137, unchanged): `{ values: { fieldKey:
 * bareValue } }` of ONLY changed, writable fields — the enriched `{value,type,options}`
 * object is NEVER sent (the server owns type/options). The response is the re-read
 * ENRICHED map (DNKAPI-0195) — refresh the form from it. Bearer-only `/v2/admin/**`.
 * Throws a typed `ApiError`.
 */
export async function putMemberProfileValues(
  contactId: string,
  values: ProfileValuesInput,
): Promise<ProfileValues> {
  const body = await request<unknown>(
    'PUT',
    `${PATH}/${encodeURIComponent(contactId)}/profile-values`,
    { json: { values } },
  );
  return parseProfileValues(body);
}

// --- Add a NET-NEW member (§D — DNKAPI-0172 SHIPPED, existing-contact only) ------

/**
 * One per-bucket credit OVERRIDE on the create (§D). Sent ONLY for a bucket the admin
 * edited away from the plan default — an omitted bucket grants the plan default (§C
 * `listPlanCredits`). `creditBucketId` is the 0052 bucket id; `amount` is the granted
 * credits; `expiresAt` is an optional bare date (`YYYY-MM-DD`, omitted = never expires).
 */
export interface CreateMemberCredit {
  creditBucketId: string;
  amount: number;
  expiresAt?: string;
}

/**
 * The Add-Member payment block (§D) — a desk RECORD, not a charge. `status:'paid'`
 * requires a `paymentMethodId` (the DNKADM-0091 method id); `pending` makes it
 * optional (member created, payment outstanding). `amountPaid` is the collected amount
 * (0 allowed for pending). `notes` is optional.
 */
export interface CreateMemberPayment {
  status: 'paid' | 'pending';
  amountPaid: number;
  paymentMethodId?: string;
  notes?: string;
}

/**
 * Input for the net-new member create (§D). Carries an existing `contactId` (from the
 * §F picker — DNKADM-0093); the shipped DNKAPI-0172 accepts an EXISTING contact ONLY
 * (the new-person / provisioning path was deferred — it needs a saas-auth member-
 * provision endpoint that doesn't exist yet). `membershipPlanId` is required; `credits`
 * are per-bucket overrides (omit a bucket to grant the plan default); `payment` is
 * always sent.
 */
export interface CreateMemberInput {
  contactId: string;
  membershipPlanId: string;
  credits?: CreateMemberCredit[];
  payment: CreateMemberPayment;
  /**
   * DNKADM-0479 (ruling 2): optional retry key, one per Add the desk means once. A re-send
   * under the same key gets the member that key already added, with no second plan, credits,
   * payment or contact event. Sent only when set.
   */
  idempotencyKey?: string;
}

/** One granted-credit row in the `201` create response (§D `credits[]`). */
interface CreatedMemberCredit {
  /** The bucket id the lot was granted on, or null on a malformed row. */
  bucketId: string | null;
  /** Credits granted on the create. */
  granted: number;
  /** ISO date/instant the lot expires, or null (never expires / absent). */
  expiresAt: string | null;
}

/** The payment record echoed in the `201` create response (§D `payment`). */
interface CreatedMemberPayment {
  id: string | null;
  status: string | null;
  amountPaid: number | null;
  /** Server-resolved method label (e.g. "Cash"), or null. */
  methodLabel: string | null;
}

/**
 * Parsed `POST /v2/admin/members` `201` response (§D). `contactId` is the new (or
 * resolved) member's external contact id — the key the dialog navigates to. Every
 * field is parsed defensively; a malformed body degrades rather than throws.
 */
export interface CreatedMember {
  /** The created member's contact id (external id) — DATA, never surfaced as "GHL …". */
  contactId: string | null;
  membershipPlanId: string | null;
  membershipStatus: string | null;
  credits: CreatedMemberCredit[];
  payment: CreatedMemberPayment | null;
}

/** Parse the granted-credits array off the create response (tolerant of wrappers). */
function parseCreatedCredits(raw: unknown): CreatedMemberCredit[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (isObject(raw) && Array.isArray(raw['credits'])) list = raw['credits'];
  return list.filter(isObject).map((row) => ({
    // Nested credit-bucket fk id — numeric on the wire (DNKADM-0064).
    bucketId: asId(row['bucketId']) ?? asId(row['creditBucketId']),
    granted: asNumber(row['granted']) ?? asNumber(row['amount']) ?? 0,
    expiresAt: asText(row['expiresAt']),
  }));
}

/** Parse the payment block off the create response, or null when absent/malformed. */
function parseCreatedPayment(raw: unknown): CreatedMemberPayment | null {
  if (!isObject(raw)) return null;
  return {
    id: asId(raw['id']),
    status: asText(raw['status']),
    amountPaid: asNumber(raw['amountPaid']),
    methodLabel: asText(raw['methodLabel']),
  };
}

/**
 * Build the create body. The "who" is the existing `contactId` (required — the shipped
 * DNKAPI-0172 accepts an existing contact only). `credits` is sent only when non-empty
 * (overrides only); each `expiresAt` is a bare `YYYY-MM-DD` (`LocalDate`) here, NOT an
 * Instant (a deliberate difference from the §B credit-adjust). `payment` always carries
 * `status`/`amountPaid`, with `paymentMethodId` only when present and `notes` only when
 * non-blank.
 */
function toCreateMemberBody(input: CreateMemberInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    contactId: input.contactId.trim(),
    membershipPlanId: input.membershipPlanId,
  };

  if (input.credits && input.credits.length > 0) {
    json['credits'] = input.credits.map((c) => {
      const credit: Record<string, unknown> = {
        creditBucketId: c.creditBucketId,
        amount: c.amount,
      };
      // Bare date (`YYYY-MM-DD`) on the wire — the date `<input>` value, sent as-is.
      if (c.expiresAt != null && c.expiresAt.trim().length > 0) {
        credit['expiresAt'] = c.expiresAt;
      }
      return credit;
    });
  }

  const payment: Record<string, unknown> = {
    status: input.payment.status,
    amountPaid: input.payment.amountPaid,
  };
  if (input.payment.paymentMethodId != null && input.payment.paymentMethodId.trim().length > 0) {
    payment['paymentMethodId'] = input.payment.paymentMethodId;
  }
  if (input.payment.notes != null && input.payment.notes.trim().length > 0) {
    payment['notes'] = input.payment.notes.trim();
  }
  json['payment'] = payment;
  if (input.idempotencyKey !== undefined) json['idempotencyKey'] = input.idempotencyKey;

  return json;
}

/**
 * `POST /v2/admin/members` (§D — DNKAPI-0172 **SHIPPED**, existing-contact only) → create
 * a NET-NEW member in ONE ATOMIC call: membership + credit lots + the payment record
 * commit together (any failure rolls all of it back — the FE never orchestrates separate
 * calls). Carries an existing `contactId` (the new-person / provisioning path was
 * deferred backend-side); `credits` are per-bucket overrides (`expiresAt` a bare date);
 * `payment` is a desk record. Bearer-only `/v2/admin/**`.
 *
 * Throws a typed `ApiError` (mapped by `toCreateMemberErrorView` — real 0172 codes):
 *  - `MEMBERSHIP_PLAN_NOT_FOUND` (404) / `NOT_FOUND` (404, bad bucket) / `INVALID_REQUEST`
 *    (400) — surfaced inline on the offending field, else form-level;
 *  - `PROVIDER_NOT_CONFIGURED` — surfaced on the payment block;
 *  - a bare `404` with NO business code — the endpoint isn't live yet (the forward-compat
 *    pre-ship state; the dialog shows "Adding members isn't available yet", never a crash).
 *
 * Parses the `201` defensively (a malformed body degrades, never throws).
 */
export async function createMember(input: CreateMemberInput): Promise<CreatedMember> {
  const body = await request<unknown>('POST', PATH, { json: toCreateMemberBody(input) });
  const o = isObject(body) ? body : {};
  return {
    // The member's OWN identity is a genuine external contact id — asText, never coerced.
    contactId: asText(o['contactId']) ?? asText(o['id']),
    membershipPlanId: asId(o['membershipPlanId']) ?? asId(o['planId']),
    membershipStatus: asText(o['membershipStatus']) ?? asText(o['status']),
    credits: parseCreatedCredits(o['credits']),
    payment: parseCreatedPayment(o['payment']),
  };
}

// --- Existing-contact picker for Add-Member (§F — DNKAPI-0173-picker + saas-auth) ---

/** v2 admin contacts base path (bearer surface) — the §F Add-Member picker. */
const CONTACTS_PATH = '/v2/admin/contacts';

/**
 * One selectable contact in the §F Add-Member "who" picker. `contactId` is the
 * external contact id (DATA, never surfaced as "GHL …") carried into the §D create.
 * `name` MAY be null (§A — identity not yet resolved on the tenant roster); the UI
 * renders the email / a neutral placeholder, NEVER an error (same posture as the
 * members list).
 */
export interface ContactSummary {
  contactId: string | null;
  name: string | null;
  email: string | null;
}

/**
 * Parsed `GET /v2/admin/contacts` page — mirrors {@link MembersPage}'s envelope
 * (`content` array, 1-indexed `page`, `total`). `total` is null when upstream omits it.
 */
export interface ContactsPage {
  contacts: ContactSummary[];
  page: number;
  size: number;
  total: number | null;
}

/** Parse one raw contact row defensively (any field may be null/absent on the wire). */
function parseContact(raw: unknown): ContactSummary {
  const o = isObject(raw) ? raw : {};
  return {
    // The contact's OWN identity is a genuine external id — asText, never coerced.
    contactId: asText(o['contactId']) ?? asText(o['id']),
    name: asText(o['name']),
    email: asText(o['email']),
  };
}

/**
 * `GET /v2/admin/contacts?search=&page=&size=` (§F — **[COMING]**) → a parsed page of
 * selectable contacts for the Add-Member "who" step. Defaults `page=1`, `size=20`;
 * mirrors {@link listMembers}'s 0-indexed wire paging (sends `page - 1`) and `content`
 * envelope (tolerating a `contacts` wrapper or a bare array). The `search` term is
 * forwarded only when non-empty (trimmed). Every field is parsed DEFENSIVELY — a
 * malformed/empty body degrades to an empty page, never a throw.
 *
 * **Forward-compatible / endpoint-absent tolerance.** §F is DOUBLY gated (a dink-api
 * picker endpoint **and** a saas-auth roster change to surface `ghlContactId`). Until
 * both land it returns `404` — represented here as a resolved **`null`** (the consumer
 * treats `null` as "picker unavailable" and falls back to the new-person path), NOT an
 * uncatchable throw. Any OTHER non-2xx still throws a typed `ApiError`.
 */
export async function searchContacts(
  search = '',
  page = 1,
  size = 20,
): Promise<ContactsPage | null> {
  const trimmed = search.trim();
  let body: unknown;
  try {
    body = await request<unknown>('GET', CONTACTS_PATH, {
      // 0-indexed on the wire (mirrors listMembers): the first UI page (1) → `?page=0`.
      query: { page: toWirePage(page), size, search: trimmed ? trimmed : undefined },
    });
  } catch (err) {
    // The endpoint isn't live yet (§F [COMING]) — representable as "unavailable" so the
    // picker simply doesn't show; never an uncatchable throw on the forward-compat path.
    if (err instanceof ApiError && err.code === 'NOT_FOUND') return null;
    throw err;
  }
  const o = isObject(body) ? body : {};
  const rawList = Array.isArray(body)
    ? body
    : Array.isArray(o['content'])
      ? o['content']
      : o['contacts'];
  const list = Array.isArray(rawList) ? rawList : [];
  const backendPage = asNumber(o['page']);
  return {
    contacts: list.map(parseContact),
    page: backendPage !== null ? toUiPage(backendPage) : page,
    size: asNumber(o['size']) ?? asNumber(o['pageLimit']) ?? size,
    total: asNumber(o['total']) ?? asNumber(o['totalElements']),
  };
}
